import { useEffect, useState } from 'react';

/** Days after the effective unification date used for post-migration behavior (e.g., NPS reactivation and Intercom attributes). */
export const UNIFICATION_MIGRATION_45_DAY_THRESHOLD = 45;

/**
 * Checks if the global variable exists and narrows the type of the
 * variable to the type of the global variable name.
 * @param name - name of the global variable
 * @returns true if the global variable exists
 */
export function isGlobal(name: string | number | undefined): name is keyof Window {
  return !!name && name in window;
}

/**
 * Injects a script tag into the DOM with the given source.
 * @param src - the source of the script tag
 * @param enabled - whether or not to inject the script tag
 * @returns the status of the script tag
 */
export function useScript(src: string, enabled = true) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    if (!enabled) {
      setStatus('ready');
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
    const updateStatus = (event: Event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };

    script.addEventListener('load', updateStatus);
    script.addEventListener('error', updateStatus);

    return () => {
      script.removeEventListener('load', updateStatus);
      script.removeEventListener('error', updateStatus);
      document.head.removeChild(script);
    };
  }, [src, enabled]);

  return { status };
}

/**
 * Swallows errors thrown by the given function.
 * @param fn - the function to swallow errors for
 * @returns the result of the function
 */
export function swallowErrors<T, A extends Array<unknown>>(fn: (...args: A) => T) {
  return (...args: A) => {
    try {
      return fn(...args);
    } catch {
      // swallow
    }
  };
}

/**
 * Groups an array of items by the given key.
 * @param array - the array to groupBy
 * @param getKey - the function to get the key from the item
 * @param getValue - the function to get the value from the item
 * @returns the object of grouped items
 */
export function groupBy<T, K extends string, V>(
  array: T[],
  getKey: (item: T) => K,
  getValue: (item: T) => V = (item) => item as unknown as V
) {
  return array.reduce<{ [key: string]: Array<V> }>((acc, item) => {
    const key = getKey(item);
    if (!Object.prototype.hasOwnProperty.call(acc, key)) {
      acc[key] = [];
    }

    acc[key].push(getValue(item));
    return acc;
  }, {});
}
